import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { trackProductView } from '../hooks/useAnalytics';
import { CustomerColorSizeSelector } from '../components/ColorSizePicker';
import { formatItemPrice } from '../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracked, setTracked] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  // Track product view - wait a moment for user to be loaded
  useEffect(() => {
    if (id && !tracked) {
      // Small delay to ensure user state is loaded from localStorage
      const timer = setTimeout(() => {
        trackProductView(id, user);
        setTracked(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [id, user, tracked]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Validate color/size selection if product has these options
    const hasColors = product.available_colors && product.available_colors.length > 0;
    const hasSizes = product.sizes && product.sizes.length > 0;
    
    if (hasColors && !selectedColor) {
      toast.error('Please select a color');
      return;
    }
    if (hasSizes && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    
    // Add product with selected variants
    const productWithVariants = {
      ...product,
      selectedColor: selectedColor,
      selectedSize: selectedSize
    };
    
    addToCart(productWithVariants);
    
    // Build toast message with selected options
    let message = `${product.name}`;
    if (selectedColor) message += ` (${selectedColor})`;
    if (selectedSize) message += ` - Size ${selectedSize}`;
    message += ' added to cart';
    
    toast.success(message);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-xl text-gray-500 mb-4">Product not found</p>
        <Link to="/products">
          <Button>Back to Products</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <Link to="/products" className="inline-flex items-center text-[#0A2342] hover:text-[#FF6B35] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </div>

          <div>
            <div className="mb-4">
              {product.in_stock ? (
                <Badge className="bg-green-100 text-green-800 border-green-200">In Stock</Badge>
              ) : (
                <Badge variant="destructive">Out of Stock</Badge>
              )}
            </div>
            
            <h1 className="text-4xl font-heading font-bold text-[#0A2342] mb-4">
              {product.name}
            </h1>
            
            <div className="text-4xl font-mono-accent font-bold text-[#0A2342] mb-6">
              {formatItemPrice(product.price, product.currency)}
            </div>

            {product.description && !product.description.includes('Add product description in admin') && (
              <p className="text-lg text-gray-700 mb-8 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            )}

            {/* Color and Size Selection */}
            {((product.available_colors && product.available_colors.length > 0) || 
              (product.sizes && product.sizes.length > 0)) && (
              <div className="mb-8 p-6 bg-white rounded-lg border border-gray-200">
                <CustomerColorSizeSelector
                  colors={product.available_colors || []}
                  customColors={product.custom_colors || []}
                  sizes={product.sizes || []}
                  sizeType={product.size_type || 'letter'}
                  selectedColor={selectedColor}
                  selectedSize={selectedSize}
                  onColorChange={setSelectedColor}
                  onSizeChange={setSelectedSize}
                />
              </div>
            )}

            <div className="bg-[#F0EFEA] p-6 rounded-lg mb-8">
              <h3 className="text-xl font-heading font-semibold text-[#0A2342] mb-3">
                Member Discounts
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Club Members (20% off):</span>
                  <span className="font-mono-accent font-bold text-[#0A2342]">
                    {formatItemPrice(product.price * 0.8, product.currency)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Pro Members (25% off):</span>
                  <span className="font-mono-accent font-bold text-[#FF6B35]">
                    {formatItemPrice(product.price * 0.75, product.currency)}
                  </span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleAddToCart}
              disabled={!product.in_stock}
              size="lg"
              className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="add-to-cart-detail-btn"
            >
              {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;